import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useUserMentionTextarea } from "../../utils/useUserMentionTextarea";
import UserMentionDropdown from "../shared/UserMentionDropdown";
import { useCommentsDock, CommentsDockHeader } from "../shared/commentsDock";
import "../../css/Document.css";
import "../../css/Comments.css";
import "../../css/SupportTicket.css";

function WorkspaceComments({ workspaceId, documentType, documentId, userInfo, workspaceTheme = 'Default', userRole = null }) {
    const { dropdownProps: mentionDropdownProps, handleTextareaChange: handleMentionChange, handleTextareaKeyDown: handleMentionKeyDown, mentionedUids, clearMentionedUids } =
        useUserMentionTextarea({ mode: "workspace", workspaceId });

    // Workspace owners and admins can delete any comment (backend enforces this too)
    const canModerateComments = ['OWNER', 'ADMIN'].includes(userRole);

    // State variables
    const [hasComments, setHasComments] = useState(true);
    const [documentComments, setDocumentComments] = useState([]);
    const [displayCreateComment, setDisplayCreateComment] = useState(false);
    const [replyStates, setReplyStates] = useState({});
    const [editStates, setEditStates] = useState({});
    const [reply, setReply] = useState("");
    const [edit, setEdit] = useState("");
    const [comment, setComment] = useState("");
    const navigate = useNavigate();

    // BBD-138: docked drawer state + scroll the new-comment form into view when it opens
    const { isOpen: dockOpen, toggle: toggleDock, open: openDock } = useCommentsDock();
    const newCommentRef = useRef(null);

    useEffect(() => {
        if (displayCreateComment && newCommentRef.current) {
            newCommentRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
        }
    }, [displayCreateComment]);

    // Fetch comments when component mounts or when props change
    useEffect(() => {
        getAllCommentsForDocument();
    }, [workspaceId, documentType, documentId, userInfo]);




    // Function to fetch all comments for the document
    const getAllCommentsForDocument = async () => {
        if (!userInfo) {
            return;
        }
        
        try {
            const token = await userInfo.getIdToken();
            
            const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/comments/${documentType}/${documentId}/`, {
                method: 'GET',
                headers: { 
                    "Content-Type": "application/json",
                    "auth-Token": `Bearer ${token}`,
              "Authorization": `${token}`,
                    "Requesting-User-Uid": userInfo.uid
                },
            });

            if (response.status === 404) {
                setHasComments(false);
                return;
            }

            if (!response.ok) {
                throw new Error(`Failed to fetch comments: ${response.status}`);
            }

            // The response is directly an array of comments
            const commentsData = await response.json();
            
            // Transform the data to match the expected format in our component
            const transformedComments = commentsData.map(comment => ({
                id: comment.id,
                content: comment.content,
                created_at: comment.created_at,
                updated_at: comment.last_updated_at,
                author_email: comment.author.email,
                author_name: comment.author.name,
                author_uid: comment.author.id.toString(), // Convert to string to match expected format
                is_reply: comment.is_reply,
                parent_comment: comment.parent_comment_id
            }));
            
            // Sort comments by creation date (newest first)
            const sortedComments = transformedComments.sort(
                (a, b) => new Date(b.created_at) - new Date(a.created_at)
            );
            
            setDocumentComments(sortedComments);
            setHasComments(sortedComments.length > 0);
        } catch (error) {
            console.error("Error fetching workspace document comments:", error);
            setHasComments(false);
        }
    };    // Toggle comment creation form
    const handlePlusIconClick = () => {
        // From a collapsed drawer, "+" always expands it and shows the form.
        if (!dockOpen) {
            openDock();
            setDisplayCreateComment(true);
            return;
        }
        setDisplayCreateComment((prevState) => !prevState);
    };

    // Create a new comment
    const handleSubmitButtonClick = async () => {
        if (!comment.trim()) {
            alert("Comment cannot be empty");
            return;
        }

        try {
            const token = await userInfo.getIdToken();
            
            const data = {
                content: comment,
                document_type: documentType,
                document_id: documentId,
                is_reply: false,
                mentionedUids: mentionedUids
            };

            const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/comments/`, {
                method: "POST",
                body: JSON.stringify(data),
                headers: {
                    "Content-Type": "application/json",
                    "auth-Token": `Bearer ${token}`,
                    "Authorization": `${token}`,
                    "Requesting-User-Uid": userInfo.uid
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to create comment: ${response.status}`);
            }

            // Reset form and refresh comments
            clearMentionedUids();
            setDisplayCreateComment(false);
            setComment("");
            await getAllCommentsForDocument();
        } catch (error) {
            console.error("Error creating comment:", error);
            alert("Failed to create comment. Please try again.");
        }
    };

    // Toggle reply form for a specific comment
    const handleReplyClick = (email, commentId, authorName) => {
        setReplyStates((prevState) => ({
            ...prevState,
            [commentId]: !prevState[commentId]
        }));
        setReply(`Reply To: ${authorName || email} | Comment ID: ${commentId}`);
    };

    // Submit a reply to a comment
    const handleReplySubmitButtonClick = async (parentCommentId) => {
        if (!reply.trim()) {
            alert("Reply cannot be empty");
            return;
        }

        try {
            const token = await userInfo.getIdToken();
            
            const data = {
                content: reply,
                document_type: documentType,
                document_id: documentId,
                is_reply: true,
                parent_comment_id: parentCommentId,
                mentionedUids: mentionedUids
            };

            const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/comments/`, {
                method: "POST",
                body: JSON.stringify(data),
                headers: {
                    "Content-Type": "application/json",
                    "auth-Token": `Bearer ${token}`,
                    "Authorization": `${token}`,
                    "Requesting-User-Uid": userInfo.uid
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to create reply: ${response.status}`);
            }

            // Reset form and refresh comments
            clearMentionedUids();
            setReplyStates((prevState) => ({
                ...prevState,
                [parentCommentId]: false
            }));
            setReply("");
            await getAllCommentsForDocument();
        } catch (error) {
            console.error("Error creating reply:", error);
            alert("Failed to create reply. Please try again.");
        }
    };

    // Toggle edit form for a specific comment
    const handleEditClick = (commentContent, commentId) => {
        setEditStates((prevState) => ({
            ...prevState,
            [commentId]: !prevState[commentId]
        }));
        setEdit(commentContent);
    };

    // Submit edited comment
    const handleEditSubmitButtonClick = async (commentId) => {
        if (!edit.trim()) {
            alert("Comment cannot be empty");
            return;
        }

        try {
            const token = await userInfo.getIdToken();
            
            const data = {
                content: edit
            };

            const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/comments/${commentId}/update/`, {
                method: "PATCH",
                body: JSON.stringify(data),
                headers: { 
                    "Content-Type": "application/json",
                    "auth-Token": `Bearer ${token}`,
              "Authorization": `${token}`,
                    "Requesting-User-Uid": userInfo.uid
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to update comment: ${response.status}`);
            }

            // Reset form and refresh comments
            setEditStates((prevState) => ({
                ...prevState,
                [commentId]: false
            }));
            setEdit("");
            await getAllCommentsForDocument();
        } catch (error) {
            console.error("Error updating comment:", error);
            alert("Failed to update comment. Please try again.");
        }
    };

    // Delete a comment
    const handleDeleteComment = async (commentId, parentCommentId = null) => {
        const confirmDelete = window.confirm("Are you sure you want to delete this comment?");
        if (!confirmDelete) return;

        try {
            const token = await userInfo.getIdToken();
            
            // For API compatibility, include parent comment info
            let url = `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/comments/${commentId}/delete/`;
            let options = {
                method: "DELETE",
                headers: { 
                    "Content-Type": "application/json",
                    "auth-Token": `Bearer ${token}`,
              "Authorization": `${token}`,
                    "Requesting-User-Uid": userInfo.uid
                }
            };
            
            // If this is a reply, some APIs require the parent ID in the body
            if (parentCommentId) {
                options.body = JSON.stringify({
                    parent_comment_id: parentCommentId
                });
            }

            const response = await fetch(url, options);

            if (!response.ok) {
                throw new Error(`Failed to delete comment: ${response.status}`);
            }

            await getAllCommentsForDocument();
        } catch (error) {
            console.error("Error deleting comment:", error);
            alert("Failed to delete comment. Please try again.");
        }
    };

    return (
        <>
        <div className={`Comment__div ${dockOpen ? "Comment__div--open" : "Comment__div--collapsed"}`}>
            <CommentsDockHeader
                count={hasComments && documentComments.length > 0 ? documentComments.length : 0}
                isOpen={dockOpen}
                onToggle={toggleDock}
                onAdd={handlePlusIconClick}
                bodyId="Comment__body-workspace"
            />

            <div className="Comment__body" id="Comment__body-workspace" hidden={!dockOpen}>

            <div className="Comment__list">
                {hasComments && documentComments.length > 0 ? (
                    <>
                        {documentComments.map((comment) => (
                            <div key={comment.id} className={`Comment__comment-area ${comment.is_reply ? 'Comment__reply' : ''}`}>
                                {!comment.is_reply && (
                                    <>
                                        <div className="Comment__meta">
                                            <div className="Comment__author-info">
                                                <i className="fa-solid fa-user Comment__user-icon"></i>
                                                <span className="Comment__author-name">{comment.author_name || comment.author_email || "Anonymous"}</span>
                                            </div>
                                            <span className="Comment__timestamp">{new Date(comment.created_at).toLocaleString()}</span>
                                        </div>
                                        <p className="Comment__content">{comment.content}</p>
                                        <div className="Comment__actions">
                                            <button className="Comment__action-btn Comment__reply-btn" onClick={() => handleReplyClick(comment.author_email, comment.id, comment.author_name)} title="Reply">
                                                <i className="fa-solid fa-reply"></i>
                                                Reply
                                            </button>
                                            {userInfo && (comment.author_email === userInfo.email) && (
                                                <button className="Comment__action-btn Comment__edit-btn" onClick={() => handleEditClick(comment.content, comment.id)} title="Edit">
                                                    <i className="fa-solid fa-pencil"></i>
                                                    Edit
                                                </button>
                                            )}
                                            {userInfo && ((comment.author_email === userInfo.email) || canModerateComments) && (
                                                <button className="Comment__action-btn Comment__delete-btn" onClick={() => handleDeleteComment(comment.id)} title={comment.author_email === userInfo.email ? "Delete" : "Delete (moderator)"}>
                                                    <i className="fa-regular fa-trash-can"></i>
                                                    Delete
                                                </button>
                                            )}
                                            <span className="Comment__id">ID: {comment.id}</span>
                                        </div>
                                    </>
                                )}
                                
                                {comment.is_reply && comment.parent_comment && (
                                    <div>
                                        <div className="Comment__reply-indicator">
                                            <i className="fa-solid fa-reply"></i>
                                            <span>Reply</span>
                                        </div>
                                        <div className="Comment__meta">
                                            <div className="Comment__author-info">
                                                <i className="fa-solid fa-user Comment__user-icon"></i>
                                                <span className="Comment__author-name">{comment.author_name || comment.author_email || "Anonymous"}</span>
                                            </div>
                                            <span className="Comment__timestamp">{new Date(comment.created_at).toLocaleString()}</span>
                                        </div>
                                        <p className="Comment__content">{comment.content}</p>
                                        <div className="Comment__actions">
                                            <button className="Comment__action-btn Comment__reply-btn" onClick={() => handleReplyClick(comment.author_email, comment.id, comment.author_name)} title="Reply">
                                                <i className="fa-solid fa-reply"></i>
                                                Reply
                                            </button>
                                            {userInfo && (comment.author_email === userInfo.email) && (
                                                <button className="Comment__action-btn Comment__edit-btn" onClick={() => handleEditClick(comment.content, comment.id)} title="Edit">
                                                    <i className="fa-solid fa-pencil"></i>
                                                    Edit
                                                </button>
                                            )}
                                            {userInfo && ((comment.author_email === userInfo.email) || canModerateComments) && (
                                                <button className="Comment__action-btn Comment__delete-btn" onClick={() => handleDeleteComment(comment.id, comment.parent_comment)} title={comment.author_email === userInfo.email ? "Delete" : "Delete (moderator)"}>
                                                    <i className="fa-regular fa-trash-can"></i>
                                                    Delete
                                                </button>
                                            )}
                                            <span className="Comment__id">ID: {comment.id}</span>
                                        </div>
                                    </div>
                                )}                                
                                {replyStates[comment.id] && (
                                    <div className="Comment__form">
                                        <textarea
                                            value={reply}
                                            className="Comment__comment-text-area"
                                            onChange={(e) => { setReply(e.target.value); handleMentionChange(e, setReply); }}
                                            onKeyDown={handleMentionKeyDown}
                                            rows={5}
                                            placeholder="Write your reply... (type @ to mention someone)"
                                            required
                                        ></textarea>
                                        <button onClick={() => handleReplySubmitButtonClick(comment.id)} className="Comment__submit-button">
                                            <i className="fa-solid fa-paper-plane"></i>
                                            Submit Reply
                                        </button>
                                    </div>
                                )}
                                
                                {editStates[comment.id] && (
                                    <div className="Comment__form">
                                        <textarea
                                            value={edit}
                                            className="Comment__comment-text-area"
                                            onChange={(e) => { setEdit(e.target.value); handleMentionChange(e, setEdit); }}
                                            onKeyDown={handleMentionKeyDown}
                                            rows={5}
                                            placeholder="Edit your comment..."
                                            required
                                        ></textarea>
                                        <button onClick={() => handleEditSubmitButtonClick(comment.id)} className="Comment__submit-button">
                                            <i className="fa-solid fa-check"></i>
                                            Save Changes
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                        
                        {displayCreateComment && (
                            <div className="Comment__form Comment__new-comment" ref={newCommentRef}>
                                <textarea
                                    value={comment}
                                    className="Comment__comment-text-area"
                                    onChange={(e) => { setComment(e.target.value); handleMentionChange(e, setComment); }}
                                    onKeyDown={handleMentionKeyDown}
                                    rows={5}
                                    placeholder="Share your thoughts... (type @ to mention someone)"
                                    required
                                ></textarea>
                                <button onClick={handleSubmitButtonClick} className="Comment__submit-button">
                                    <i className="fa-solid fa-paper-plane"></i>
                                    Post Comment
                                </button>
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        <div className="Comment__empty-state">
                            <i className="fa-regular fa-comments Comment__empty-icon"></i>
                            <p className="Comment__empty-text">No comments yet. Be the first to share your thoughts!</p>
                        </div>
                        {displayCreateComment && (
                            <div className="Comment__form Comment__new-comment" ref={newCommentRef}>
                                <textarea
                                    value={comment}
                                    className="Comment__comment-text-area"
                                    onChange={(e) => { setComment(e.target.value); handleMentionChange(e, setComment); }}
                                    onKeyDown={handleMentionKeyDown}
                                    rows={5}
                                    placeholder="Share your thoughts... (type @ to mention someone)"
                                    required
                                ></textarea>
                                <button onClick={handleSubmitButtonClick} className="Comment__submit-button">
                                    <i className="fa-solid fa-paper-plane"></i>
                                    Post Comment
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            </div>
        </div>
        <UserMentionDropdown {...mentionDropdownProps} />
        </>
    );}

export default WorkspaceComments